import { ZIP_PATTERN, PHONE_PATTERN, BIN_PATTERN, EMAIL_PATTERN, isValidOrBlank, filled } from "./patterns";
import {
    isMedicalInsuranceSkipped,
    isPharmacyInsuranceSkipped,
    providerFormatErrors,
    siteOfCareRequired,
    isSiteOfCareStarted,
    siteOfCareFormatErrors,
} from "./enrollment";

const INDICATIONS = ["1", "2", "3", "4"];

const eligibilityErrors = (a = {}) => {
    const errors = [];
    if (a.q1 !== "yes") errors.push("Eligibility question 1 must be answered Yes.");
    if (a.q2 !== "yes") errors.push("Eligibility question 2 must be answered Yes.");
    if (!INDICATIONS.includes(String(a.q3 ?? ""))) errors.push("Eligibility question 3 must be an FDA-approved indication.");
    if (a.q4 !== "yes") errors.push("Eligibility question 4 must be answered Yes.");
    if (a.q5 === "yes") {
        if (a.q5b !== "yes" && a.q5b !== "no") {
            errors.push("Eligibility question 5: please indicate whether the patient has a Medicare card.");
        } else if (a.q5b === "yes" && !filled(a.q5c)) {
            errors.push("Eligibility question 5: a Medicare Number is required.");
        }
    } else if (a.q5 !== "no") {
        errors.push("Eligibility question 5 must be answered.");
    }
    if (!/^[A-Z]{2}$/.test(String(a.q6 ?? ""))) errors.push("Eligibility question 6: please select a state.");
    if (a.q7 !== "no") errors.push("Eligibility question 7 must be answered No.");
    if (a.q8 !== "no") errors.push("Eligibility question 8 must be answered No.");
    if (a.q9 !== "agree") errors.push("Eligibility question 9 must be Agree.");
    if (a.q10 !== "agree") errors.push("Eligibility question 10 must be Agree.");
    return errors;
};

const patientErrors = (d = {}) => {
    const errors = [];
    const required = {
        "First name": d.first_name,
        "Last name": d.last_name,
        Gender: d.gender,
        "Date of birth": d.dob,
        Address: d.address?.address1,
        City: d.address?.city,
        State: d.address?.state,
        "ZIP code": d.address?.zip_code,
        "Phone number": d.phone_number,
    };
    Object.entries(required).forEach(([label, value]) => {
        if (!filled(value)) errors.push(`Patient Information: ${label} is required.`);
    });
    if (!isValidOrBlank(ZIP_PATTERN, d.address?.zip_code)) {
        errors.push("Patient Information: please enter a valid 5-digit ZIP code.");
    }
    if (!isValidOrBlank(PHONE_PATTERN, d.phone_number)) {
        errors.push("Patient Information: please enter a valid 10-digit phone number.");
    }
    if (filled(d.email) && !EMAIL_PATTERN.test(String(d.email).trim())) {
        errors.push("Patient Information: please enter a valid email address.");
    }
    return errors;
};

const lapErrors = (lap = {}) => {
    const errors = [];
    const required = {
        "First name": lap.first_name,
        "Last name": lap.last_name,
        Address: lap.address?.address1,
        City: lap.address?.city,
        State: lap.address?.state,
        "ZIP code": lap.address?.zip_code,
        "Phone number": lap.phone,
    };
    Object.entries(required).forEach(([label, value]) => {
        if (!filled(value)) errors.push(`Legally Authorized Person: ${label} is required.`);
    });
    if (!isValidOrBlank(ZIP_PATTERN, lap.address?.zip_code)) {
        errors.push("Legally Authorized Person: please enter a valid 5-digit ZIP code.");
    }
    if (!isValidOrBlank(PHONE_PATTERN, lap.phone)) {
        errors.push("Legally Authorized Person: please enter a valid 10-digit phone number.");
    }
    return errors;
};

const medicalInsuranceErrors = (mi = {}) => {
    const errors = [];
    const required = {
        "Company name": mi.name,
        "Plan type": mi.plan_type,
        "Group number": mi.group_number,
        "Member number": mi.member_number,
        "Effective date": mi.effective_date,
    };
    Object.entries(required).forEach(([label, value]) => {
        if (!filled(value)) errors.push(`Commercial Medical Insurance Information: ${label} is required.`);
    });
    if (filled(mi.bin) !== filled(mi.pcn)) {
        errors.push("Commercial Medical Insurance Information: BIN and PCN must both be entered or both be left blank.");
    }
    if (!isValidOrBlank(BIN_PATTERN, mi.bin)) {
        errors.push("Commercial Medical Insurance Information: please enter a valid 6-digit BIN.");
    }
    return errors;
};

const pharmacyInsuranceErrors = (rx = {}) => {
    const errors = [];
    const required = {
        "Company name": rx.name,
        "Group number": rx.group_number,
        "Member number": rx.member_number,
        BIN: rx.bin,
        PCN: rx.pcn,
    };

    if (!Object.values(required).some(filled)) return errors;

    Object.entries(required).forEach(([label, value]) => {
        if (!filled(value)) errors.push(`Commercial Pharmacy Insurance Information: ${label} is required.`);
    });
    if (!isValidOrBlank(BIN_PATTERN, rx.bin)) {
        errors.push("Commercial Pharmacy Insurance Information: please enter a valid 6-digit BIN.");
    }
    return errors;
};

const patientServicesErrors = (opt, email) => {
    const errors = [];
    if (opt !== "Y" && opt !== "N") {
        errors.push("Sign Up for Additional Patient Services: please answer Yes or No.");
        return errors;
    }
    if (opt === "Y") {
        if (!filled(email)) errors.push("Sign Up for Additional Patient Services: an email address is required.");
        else if (!EMAIL_PATTERN.test(String(email).trim())) {
            errors.push("Sign Up for Additional Patient Services: please enter a valid email address.");
        }
    }
    return errors;
};

const providerErrors = (p = {}) => {
    const errors = [];
    const required = {
        "First name": p.first_name,
        "Last name": p.last_name,
        Address: p.address?.address1,
        City: p.address?.city,
        State: p.address?.state,
        "ZIP code": p.address?.zip_code,
        "Phone number": p.phones?.phone,
    };
    Object.entries(required).forEach(([label, value]) => {
        if (!filled(value)) errors.push(`Provider Information: ${label} is required.`);
    });
    return [...errors, ...providerFormatErrors(p)];
};

const siteOfCareErrors = (s = {}) => {
    if (!isSiteOfCareStarted(s)) return [];

    const errors = [];
    Object.entries(siteOfCareRequired(s)).forEach(([label, value]) => {
        if (!filled(value)) errors.push(`Site of Care: ${label} is required.`);
    });
    return [...errors, ...siteOfCareFormatErrors(s)];
};

export const validateEnrollmentReview = ({
    enrollment_data = {},
    eligibility_answers = {},
    provider_data = {},
    site_of_care = {},
    email_opt_in = {},
    lapRequired = false,
    checkPatientServices = false,
} = {}) => {
    const medicalSkipped = isMedicalInsuranceSkipped();
    const pharmacySkipped = isPharmacyInsuranceSkipped();

    return [
        ...eligibilityErrors(eligibility_answers),
        ...patientErrors(enrollment_data),
        ...(lapRequired ? lapErrors(enrollment_data.lap) : []),
        ...(medicalSkipped ? [] : medicalInsuranceErrors(enrollment_data.insurance?.medical_insurance)),
        ...(pharmacySkipped ? [] : pharmacyInsuranceErrors(enrollment_data.insurance?.rx_insurance)),
        ...(checkPatientServices ? patientServicesErrors(email_opt_in?.opt, enrollment_data.patient_services_email) : []),
        ...providerErrors(provider_data),
        ...siteOfCareErrors(site_of_care),
    ];
};

